import { useCallback, useMemo } from 'react';
import { create } from 'zustand';

import { axiosInstanceV3 } from '@mais-retorno-front/configs/axios-config';
import themeVariables from '@mais-retorno-front/theme/theme-variables';
import {
  createComparisonDrawDownSerie,
  createComparisonVolatilitySerie,
  createProfitSeries,
  createSheetFundNetWorthSerieChart,
  createSheetFundsQuotaHoldersSerieChart,
} from '@mais-retorno-front/data-access-test-2/helpers';
import { IChartSeries } from '@mais-retorno-front/models/chart-serie';

interface IUseStore {
  series: any;
  loading: boolean;
  setLoading: (isLoading: boolean) => void;
  setSeries: (stats: any) => void;
}

const useStore = create<IUseStore>((set) => ({
  series: null,
  loading: true,
  setLoading: (isLoading: boolean) => set({ loading: isLoading }),
  setSeries: (series: any) => set({ series }),
}));

export const useSheetFundSeries = () => {
  const series = useStore((state) => state.series);
  const loading = useStore((state) => state.loading);
  const setLoading = useStore((state) => state.setLoading);
  const setSeries = useStore((state) => state.setSeries);

  const profitSeries = useMemo<IChartSeries[]>(
    () => (!series ? [] : createProfitSeries([series])),
    [series]
  );
  const netWorthSeries = useMemo<IChartSeries[]>(
    () => (!series ? [] : createSheetFundNetWorthSerieChart(series)),
    [series]
  );
  const quotaHolderSeries = useMemo<IChartSeries[]>(
    () => (!series ? [] : createSheetFundsQuotaHoldersSerieChart(series)),
    [series]
  );
  const drawDownSeries = useMemo<IChartSeries[]>(
    () => (!series ? [] : createComparisonDrawDownSerie([series])),
    [series]
  );
  const volatilitySeries = useMemo<IChartSeries[]>(
    () => (!series ? [] : createComparisonVolatilitySerie([series])),
    [series]
  );

  const getSeries = useCallback(
    async ({
      fundSlug,
      start_date,
      end_date,
    }: {
      fundSlug: string;
      start_date: number;
      end_date: number;
    }) => {
      setLoading(true);

      try {
        const { data: quotes } = await axiosInstanceV3.get(
          `/funds/quotes/${fundSlug}`,
          {
            params: { start_date, end_date },
          }
        );

        setSeries({ ...quotes, color: themeVariables.colors.secondary });
      } catch (error) {
        setSeries(null);
      }

      setLoading(false);
    },
    [setSeries, setLoading]
  );

  const changePeriod = () => {};

  return {
    loadingSeries: loading,
    getSeries,
    profitSeries,
    netWorthSeries,
    quotaHolderSeries,
    drawDownSeries,
    volatilitySeries,
  };
};
